import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useRef,
  useCallback,
} from "react";
import {motion, AnimatePresence} from "framer-motion";
import {cn} from "@/lib/utils";

interface DrawerContextType {
  openDrawer: (id: string) => void;
  closeDrawer: (id: string) => void;
  isDrawerOpen: (id: string) => boolean;
  Drawer: React.FC<DrawerProps>;
  registerDrawerRef: (id: string, ref: React.RefObject<HTMLDivElement>) => void;
  drawerRefs: Map<string, React.RefObject<HTMLDivElement>>;
  setDrawerClickOutsideDisabled: (id: string, disabled: boolean) => void;
}

interface DrawerProps {
  id: string;
  children: ReactNode;
  className?: string;
  position?: "right" | "left";
  width?: string;
  height?: string;
  disableClickOutside?: boolean;
  offset?: {
    top?: string;
    bottom?: string;
    left?: string;
    right?: string;
  };
}

const DrawerContext = createContext<DrawerContextType | undefined>(undefined);

export const useDrawer = () => {
  const context = useContext(DrawerContext);
  if (!context) {
    throw new Error("useDrawer must be used within a DrawerProvider");
  }
  return context;
};

const Drawer: React.FC<DrawerProps> = ({
  id,
  children,
  className,
  position = "right",
  width = "w-110",
  height = "h-full",
  disableClickOutside = false,
  offset = {},
}) => {
  const {isDrawerOpen, registerDrawerRef, setDrawerClickOutsideDisabled} =
    useDrawer();
  const drawerRef = useRef<HTMLDivElement>(null);
  const isOpen = isDrawerOpen(id);

  // Register the drawer ref with the provider and set click outside disabled state
  useEffect(() => {
    if (isOpen) {
      registerDrawerRef(id, drawerRef);
      setDrawerClickOutsideDisabled(id, disableClickOutside);
    }
  }, [
    isOpen,
    id,
    registerDrawerRef,
    disableClickOutside,
    setDrawerClickOutsideDisabled,
  ]);

  // Build dynamic position classes with offset support
  const getPositionClasses = () => {
    const baseClasses = "fixed";
    const positionClasses = position === "left" ? "left-2" : "right-2";
    const topOffset = offset.top || "top-[103px]";
    const bottomOffset = offset.bottom || "bottom-2";

    return `${baseClasses} ${positionClasses} ${topOffset} ${bottomOffset}`;
  };

  // Build custom styles for offset overrides
  const getCustomStyles = () => {
    const styles: React.CSSProperties = {};

    const hasUnit = (value: string) =>
      value.includes("px") ||
      value.includes("%") ||
      value.includes("rem") ||
      value.includes("em");

    if (offset.left) {
      styles.left = hasUnit(offset.left) ? offset.left : `${offset.left}px`;
    }
    if (offset.right) {
      styles.right = hasUnit(offset.right) ? offset.right : `${offset.right}px`;
    }
    if (offset.top) {
      styles.top = hasUnit(offset.top) ? offset.top : `${offset.top}px`;
    }
    if (offset.bottom) {
      styles.bottom = hasUnit(offset.bottom)
        ? offset.bottom
        : `${offset.bottom}px`;
    }

    return styles;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key={id}
          initial={{opacity: 0, x: position === "left" ? -32 : 16}}
          animate={{opacity: 1, x: 0}}
          exit={{opacity: 0, x: position === "left" ? -16 : 16}}
          transition={{duration: 0.1, ease: "easeInOut"}}
          className={cn(getPositionClasses(), "z-[9999]")}
          style={getCustomStyles()}
        >
          <div
            ref={drawerRef}
            className={cn(
              "drawer bg-white border rounded-2xl",
              width,
              height,
              className,
            )}
          >
            <div className="flex flex-col max-h-full h-full">{children}</div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const DrawerProvider: React.FC<{children: ReactNode}> = ({children}) => {
  const [openDrawers, setOpenDrawers] = useState<Set<string>>(new Set());
  const [drawerRefs, setDrawerRefs] = useState<
    Map<string, React.RefObject<HTMLDivElement>>
  >(new Map());
  const [clickOutsideDisabled, setClickOutsideDisabled] = useState<Set<string>>(
    new Set(),
  );

  const openDrawer = useCallback((id: string) => {
    setOpenDrawers(prev => new Set(prev).add(id));
  }, []);

  const closeDrawer = useCallback((id: string) => {
    setOpenDrawers(prev => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
    // Clean up the ref when drawer closes
    setDrawerRefs(prev => {
      const newMap = new Map(prev);
      newMap.delete(id);
      return newMap;
    });
    // Clean up click outside disabled state when drawer closes
    setClickOutsideDisabled(prev => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
  }, []);

  const isDrawerOpen = useCallback(
    (id: string) => {
      return openDrawers.has(id);
    },
    [openDrawers],
  );

  const registerDrawerRef = useCallback(
    (id: string, ref: React.RefObject<HTMLDivElement>) => {
      setDrawerRefs(prev => new Map(prev).set(id, ref));
    },
    [],
  );

  const setDrawerClickOutsideDisabled = useCallback(
    (id: string, disabled: boolean) => {
      setClickOutsideDisabled(prev => {
        const newSet = new Set(prev);
        if (disabled) {
          newSet.add(id);
        } else {
          newSet.delete(id);
        }
        return newSet;
      });
    },
    [],
  );

  // Handle click outside for all open drawers
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      // Check if click is outside any open drawer
      for (const [drawerId, drawerRef] of drawerRefs.entries()) {
        if (
          isDrawerOpen(drawerId) &&
          !clickOutsideDisabled.has(drawerId) &&
          drawerRef.current &&
          !drawerRef.current.contains(target)
        ) {
          closeDrawer(drawerId);
          break; // Close only the first drawer that matches
        }
      }
    };

    if (openDrawers.size > 0) {
      const iframeDoc = (window as any).__flowBuilderIframeDoc;
      const targetDoc = iframeDoc;

      targetDoc.addEventListener("click", handleClickOutside);

      return () => {
        targetDoc.removeEventListener("click", handleClickOutside);
      };
    }
  }, [
    openDrawers,
    drawerRefs,
    isDrawerOpen,
    closeDrawer,
    clickOutsideDisabled,
  ]);

  const value: DrawerContextType = {
    openDrawer,
    closeDrawer,
    isDrawerOpen,
    Drawer,
    registerDrawerRef,
    drawerRefs,
    setDrawerClickOutsideDisabled,
  };

  return (
    <DrawerContext.Provider value={value}>{children}</DrawerContext.Provider>
  );
};
